'use client';

import Link from 'next/link';
import { useState } from 'react';
import { Provider, Country, Category } from '@/types';
import { useLanguage } from '@/contexts/LanguageContext';
import { getCountryFlag } from '@/lib/flags';
import LeadCaptureModal from './LeadCaptureModal';

interface ProviderDetailProps {
  provider: Provider;
  countries: Country[];
  category: Category | undefined;
}

function sanitizeText(text: string): string {
  if (!text) return '';
  return text
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[.*?\]/g, '')
    .replace(/={3,}/g, '')
    .replace(/-{3,}/g, '')
    .replace(/blob:http[^\s]*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function ProviderDetail({ provider, countries, category }: ProviderDetailProps) {
  const { t } = useLanguage();
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(provider.tier !== 'basic');

  const providerCountries = countries.filter(c => provider.countries.includes(c.code));
  const cleanDescription = sanitizeText(provider.description);

  const handleLeadSubmit = () => {
    setContactRevealed(true);
    setShowLeadModal(false);
  };

  const getTierBadge = () => {
    switch (provider.tier) {
      case 'professional':
        return <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.pro')}</span>;
      case 'premium':
        return <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.premium')}</span>;
      case 'enterprise':
        return <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.enterprise')}</span>;
      default:
        return null;
    }
  };

  return (
    <>
      <main className="py-12 bg-gray-50 dark:bg-[#0f172a] min-h-screen transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link href="/" className="text-[#d4a574] hover:text-[#e8c9a3] mb-6 inline-block">
            ← Back to Directory
          </Link>

          {/* Upgrade CTA for Basic Tier */}
          {provider.tier === 'basic' && !contactRevealed && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-bold text-[#1e3a5f] dark:text-white mb-1">Are you this business owner?</h3>
                  <p className="text-gray-600 dark:text-gray-400">Upgrade to show your contact info directly and get more leads</p>
                </div>
                <Link
                  href="/pricing"
                  className="bg-[#d4a574] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#e8c9a3] transition whitespace-nowrap"
                >
                  Upgrade Now
                </Link>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-[#1e293b] rounded-xl shadow-lg p-8 mb-8 transition-colors duration-300">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-[#1e3a5f] to-[#152a45] dark:from-[#d4a574] dark:to-[#e8c9a3] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-[#d4a574] dark:text-[#1e3a5f] text-3xl">🏢</span>
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-[#1e3a5f] dark:text-white mb-2">{provider.name}</h1>
                {category && (
                  <Link href={`/category/${category.id}`} className="text-[#d4a574] hover:underline">
                    {category.name}
                  </Link>
                )}
              </div>
              <div className="flex flex-col gap-2 items-end">
                {provider.featured && (
                  <span className="bg-[#d4a574] text-white text-xs font-bold px-3 py-1 rounded-full">{t('provider.featured')}</span>
                )}
                {getTierBadge()}
                {provider.tier === 'enterprise' && (
                  <span className="text-green-600 dark:text-green-400 text-xs font-semibold flex items-center gap-1">
                    ✓ {t('provider.verified')}
                  </span>
                )}
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
              <h2 className="text-xl font-bold text-[#1e3a5f] dark:text-white mb-4">Description</h2>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{cleanDescription}</p>
            </div>

            {/* Contact Information - Conditional based on tier */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {provider.tier === 'basic' && !contactRevealed ? (
                <>
                  {/* Blurred contact info with CTA */}
                  <div className="col-span-2 bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
                    <div className="mb-4">
                      <div className="blur-sm select-none pointer-events-none space-y-3">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 justify-center">
                          <span>📞</span>
                          <span>+XXX XXXX XXXX</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 justify-center">
                          <span>✉️</span>
                          <span>contact@example.com</span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-[#1e3a5f] dark:text-white mb-2">
                      Contact Information Available
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Fill out a quick form to view contact details and connect with this provider
                    </p>
                    <button
                      onClick={() => setShowLeadModal(true)}
                      className="bg-[#d4a574] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#e8c9a3] transition"
                    >
                      Get Contact Info
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Show actual contact info for Professional+ or after lead capture */}
                  {provider.phone && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h3 className="font-semibold text-[#1e3a5f] dark:text-white mb-2">Phone</h3>
                      <a href={`tel:${provider.phone}`} className="text-[#d4a574] hover:underline flex items-center gap-2">
                        <span>📞</span>
                        <span>{provider.phone}</span>
                      </a>
                    </div>
                  )}

                  {provider.email && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h3 className="font-semibold text-[#1e3a5f] dark:text-white mb-2">Email</h3>
                      <a href={`mailto:${provider.email}`} className="text-[#d4a574] hover:underline flex items-center gap-2">
                        <span>✉️</span>
                        <span>{provider.email}</span>
                      </a>
                    </div>
                  )}

                  {provider.website && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h3 className="font-semibold text-[#1e3a5f] dark:text-white mb-2">Website</h3>
                      <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-[#d4a574] hover:underline flex items-center gap-2">
                        <span>🌐</span>
                        <span>{t('provider.visitWebsite')}</span>
                      </a>
                    </div>
                  )}

                  {(provider.tier === 'premium' || provider.tier === 'enterprise') && provider.address && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h3 className="font-semibold text-[#1e3a5f] dark:text-white mb-2">Address</h3>
                      <p className="text-gray-700 dark:text-gray-300">{provider.address}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Services for Premium/Enterprise */}
            {(provider.tier === 'premium' || provider.tier === 'enterprise') && provider.services.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
                <h3 className="font-semibold text-[#1e3a5f] dark:text-white mb-3">{t('provider.services')}</h3>
                <div className="flex flex-wrap gap-2">
                  {provider.services.map((service, idx) => (
                    <span key={idx} className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-lg text-sm">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="font-semibold text-[#1e3a5f] dark:text-white mb-3">Operating in</h3>
              <div className="flex flex-wrap gap-2">
                {providerCountries.map((country) => (
                  <Link
                    key={country.code}
                    href={`/country/${country.code}`}
                    className="inline-flex items-center gap-2 bg-[#1e3a5f] dark:bg-[#d4a574] text-white px-4 py-2 rounded-lg hover:bg-[#152a45] dark:hover:bg-[#e8c9a3] transition"
                  >
                    <span>{getCountryFlag(country.code)}</span>
                    <span>{t(`country.${country.code}`)}</span>
                  </Link>
                ))}
              </div>
            </div>

            {provider.website && contactRevealed && (
              <div className="mt-8 text-center">
                <a
                  href={provider.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-[#d4a574] text-white px-12 py-4 rounded-xl font-bold text-lg hover:bg-[#e8c9a3] transition"
                >
                  Get Free Quote
                </a>
              </div>
            )}
          </div>

          {category && (
            <div className="text-center">
              <Link
                href={`/category/${category.id}`}
                className="text-[#d4a574] hover:text-[#e8c9a3] font-semibold"
              >
                View more {category.name} providers →
              </Link>
            </div>
          )}
        </div>
      </main>

      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        providerName={provider.name}
        onSubmit={handleLeadSubmit}
      />
    </>
  );
}
